import { useEffect, useMemo, useState } from "react";
import { Box, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { GridColDef, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import { FormDate, FormText, IconButton } from "@itk/mui-components";
import { informationPageInvoiceHistorySectionK, useInformationPageInvoiceHistorySectionT } from "src/translation-hooks/information-page-invoice-history-section";
import { useGetCustomerId, useUserSettings } from "src/hooks";
import { FormInclude, FormState, handleOnFieldChange, minDate, useForm } from "@itk/react-form";
import { FormType, QUERY_PARAMS, TQueryParamsValues } from "./invoice-history-types";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { invoiceHistoryActions } from "./invoice-history-store";
import { convertToDateOnly, formatToDateOnly, thousandsSeparator } from "src/utils";
import { useCurrencyTranslation } from "src/translations-enums-hooks/useCurrencyTranslation";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { DataGridWithIdentity, DekoxIcon } from "src/components";
import { InvoiceLinesDialog } from "./components";
import { Model_InvoicePost, Shared_SystemSettings } from "src/backend/openapi/v1";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";
import { ConvertDatesToStrings, hasValue, useExceptFirstEffect } from "@itk/typescript-utils";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { applicationActions } from "src/components/application-store";
import { IDENTITY_SETTINGS_KEYS } from "src/utils/types/identity-settings-types";
import { GenericListSortingDirection } from "src/components/datagrid-with-identity/datagrid-with-identity-types";

const mapStateToProps = (state: RootState) => ({
  invoicesState: state.pageReducer.informationPageReducer.invoiceHistoryReducer.invoicesState,
  invoiceLinesDialogState: state.pageReducer.informationPageReducer.invoiceHistoryReducer.invoiceLinesDialogState,
  systemSettings: state.generalReducer.applicationReducer.systemSettings,
  identitySettings: state.generalReducer.applicationReducer.identitySettings
});

const ownActions = {
  getInvoicesRequest: invoiceHistoryActions.getInvoicesRequest,
  setInvoiceLinesDialog: invoiceHistoryActions.setInvoiceLinesDialog,
  setIdentitySettingsRequest: applicationActions.setIdentitySettingsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const InvoiceHistoryBase = (props: Props) => {
  const { getInvoicesRequest, invoicesState, invoiceLinesDialogState, setInvoiceLinesDialog, systemSettings, identitySettings, setIdentitySettingsRequest } = props;

  const dataGridSettings = identitySettings[IDENTITY_SETTINGS_KEYS.INVOICE_HISTORY_DATA_GRID];

  const theme = useTheme();
  const { language } = useUserSettings();
  const locale = language === "da" ? "da-dk" : "en";

  const informationPageInvoiceHistorySectionT = useInformationPageInvoiceHistorySectionT();
  const genericTranslationT = useGenericTranslationsT();

  const { customerId } = useGetCustomerId();
  const { getCurrencyTranslation } = useCurrencyTranslation();

  const { dateFormat } = useUserSettings();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryParams = Object.fromEntries(searchParams.entries()) as Record<TQueryParamsValues, string>;

  const [debouncedSearch, setDebouncedSearch] = useState(queryParams.wildCardSearch ?? "");

  const [paginationModel, setPaginationModel] = useState({
    pageSize: queryParams.pageSize ? Number(queryParams.pageSize) : dataGridSettings?.pageSize ? Number(dataGridSettings.pageSize) : 50,
    page: queryParams.page ? Number(queryParams.page) : 0
  });
  const [sortModel, setSortModel] = useState<GridSortModel[number] | undefined>(
    dataGridSettings && hasValue(dataGridSettings?.sortColumnId) && hasValue(dataGridSettings?.sortDirection)
      ? {
          field: dataGridSettings.sortColumnId,
          sort: dataGridSettings.sortDirection === GenericListSortingDirection.Ascending ? "asc" : "desc"
        }
      : undefined
  );

  const defaultTimeSpan = useMemo(() => {
    if (systemSettings?.[Shared_SystemSettings.DefaultTimeSpan]) {
      return systemSettings[Shared_SystemSettings.DefaultTimeSpan].split(" ");
    }
    return [];
  }, [systemSettings]);

  const initialData = useMemo((): FormType => {
    let fromDate: Date | null =
      defaultTimeSpan.length === 2 && !Number.isNaN(defaultTimeSpan[0])
        ? moment()
            .subtract(...defaultTimeSpan)
            .toDate()
        : moment().subtract(7, "days").toDate();
    if (searchParams.has(QUERY_PARAMS.FROM_DATE)) fromDate = queryParams.fromDate ? moment(queryParams.fromDate).toDate() : null;

    let toDate: Date | null = moment().toDate();
    if (searchParams.has(QUERY_PARAMS.TO_DATE)) toDate = queryParams.toDate ? moment(queryParams.toDate).toDate() : null;

    return {
      wildcardSearch: queryParams.wildCardSearch ?? null,
      fromDate,
      toDate
    };
  }, [defaultTimeSpan]);

  const include = useMemo((): FormInclude<FormType> => {
    return {
      fromDate: true,
      toDate: true,
      wildcardSearch: true
    };
  }, [defaultTimeSpan]);

  const initFormController = useMemo(
    () => ({
      toDate: {
        validators: (state: FormState<FormType>) =>
          state.fromDate.value && state.toDate.value && moment(state.fromDate.value).isAfter(moment(state.toDate.value)) ? [minDate(state.fromDate.value)] : []
      }
    }),
    []
  );

  const { formState, formController, getValues } = useForm<FormType>(initialData, initFormController, include);

  const getInvoices = () => {
    if (!customerId) return;

    const values = getValues();
    const payload: ConvertDatesToStrings<Model_InvoicePost> = {
      ...paginationModel,
      sortColumnId: sortModel?.field,
      sortDirection: sortModel?.sort && sortModel?.sort === "asc" ? GenericListSortingDirection.Ascending : GenericListSortingDirection.Descending,
      fromInvoiceDate: convertToDateOnly(values?.fromDate),
      toInvoiceDate: convertToDateOnly(values?.toDate),
      wildCardSearch: debouncedSearch
    };
    getInvoicesRequest(customerId, payload, false);
  };

  useEffect(() => {
    return () => {
      setInvoiceLinesDialog(undefined);
    };
  }, []);

  useEffect(() => {
    if (formState.toDate.value !== null) {
      handleOnFieldChange(formState.toDate.value, formController.toDate);
    }
  }, [formState.fromDate.value]);

  // Get invoices first time
  useEffect(() => {
    getInvoices();
  }, [defaultTimeSpan]);

  // get invoices after state change
  useEffectAfterMount(() => {
    getInvoices();
  }, [paginationModel.page, paginationModel.pageSize, sortModel?.field, sortModel?.sort, debouncedSearch, formState.fromDate.value, formState.toDate.value]);

  // Debounced wildcard search
  useExceptFirstEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(getValues()?.wildcardSearch || "");
    }, 500); // debounce delay

    return () => clearTimeout(handler);
  }, [formState.wildcardSearch.value]);

  useEffect(() => {
    if (queryParams.invoiceId) {
      setInvoiceLinesDialog({ selectedInvoiceId: queryParams.invoiceId });
    }
  }, [queryParams.invoiceId]);

  // Update query params
  useEffect(() => {
    const values = getValues();

    const fromDate = convertToDateOnly(values?.fromDate) ?? "";
    const toDate = convertToDateOnly(values?.toDate) ?? "";

    const qParams: Partial<Record<TQueryParamsValues, string>> = {
      fromDate,
      toDate,
      page: paginationModel.page.toString(),
      pageSize: paginationModel.pageSize.toString(),
      wildCardSearch: debouncedSearch,
      invoiceId: invoiceLinesDialogState?.selectedInvoiceId?.toString() || ""
    };
    Object.entries(qParams).forEach(([key, value]) => {
      if (["fromDate", "toDate"].includes(key)) return;
      if (!value) delete qParams[key as TQueryParamsValues];
    });

    setSearchParams(qParams);
  }, [paginationModel.page, paginationModel.pageSize, setSearchParams, getValues, convertToDateOnly, debouncedSearch, invoiceLinesDialogState]);

  const columns: GridColDef[] = [
    { field: "navInvoiceNumber", headerName: informationPageInvoiceHistorySectionT.invoiceNo, flex: 1.5 },
    {
      field: "invoiceDate",
      headerName: informationPageInvoiceHistorySectionT.invoiceDate,
      flex: 1.5,
      renderCell: params => <Typography>{formatToDateOnly(params.row.invoiceDate, dateFormat)}</Typography>
    },
    {
      field: "currency",
      headerName: informationPageInvoiceHistorySectionT.currency,
      flex: 1.5,
      renderCell: params => <Typography>{getCurrencyTranslation(params.row.currencyId)}</Typography>
    },
    {
      field: "totalAmount",
      headerName: informationPageInvoiceHistorySectionT.totalAmount,
      flex: 1.5,
      align: "right",
      headerAlign: "right",
      renderCell: params => <Typography>{thousandsSeparator(params.row.totalAmount, locale)}</Typography>
    },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Stack direction="row" justifyContent="flex-end" flex={1}>
          <IconButton
            k={informationPageInvoiceHistorySectionK.invoiceLines}
            onClick={() => {
              setInvoiceLinesDialog({ selectedInvoiceId: row.navInvoiceId });
            }}
          >
            <DekoxIcon icon="invoice" />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Stack spacing={3} flex={1} overflow="auto" paddingY={2} width="100%">
      <Stack direction="row" gap={2} height={60}>
        <FormText
          formFieldState={formState.wildcardSearch}
          formFieldController={formController.wildcardSearch}
          t={genericTranslationT}
          k={genericTranslationsK}
          field="search"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <DekoxIcon icon="search" />
                </InputAdornment>
              )
            }
          }}
          sx={{
            "& .MuiInputBase-root .MuiInputBase-input": {
              minWidth: 400
            }
          }}
        />
        <Stack direction="row" gap={1} width="100%">
          <Box>
            <FormDate
              k={informationPageInvoiceHistorySectionK}
              key={formState.fromDate.value?.toString()}
              field="fromDate"
              t={informationPageInvoiceHistorySectionT}
              formFieldState={formState.fromDate}
              formFieldController={formController.fromDate}
              format={dateFormat}
              sx={{ width: 200 }}
            />
          </Box>
          <Typography alignContent="center">-</Typography>
          <Box>
            <FormDate
              k={informationPageInvoiceHistorySectionK}
              key={formState.toDate.value?.toString()}
              field="toDate"
              t={informationPageInvoiceHistorySectionT}
              formFieldState={formState.toDate}
              formFieldController={formController.toDate}
              format={dateFormat}
              minDate={formState.fromDate.value ? moment(formState.fromDate.value).add(1, "day").toDate() : undefined}
              sx={{ width: 200 }}
            />
          </Box>
        </Stack>
      </Stack>

      <DataGridWithIdentity
        dataGridSettings={dataGridSettings}
        handleSaveSettings={value => {
          setIdentitySettingsRequest(IDENTITY_SETTINGS_KEYS.INVOICE_HISTORY_DATA_GRID, JSON.stringify(value));
        }}
        key={invoicesState.loading}
        rows={invoicesState.loading === "has-loaded" ? invoicesState.data.data : undefined}
        rowCount={invoicesState.loading === "has-loaded" ? invoicesState.data.totalCount : 0}
        columns={columns}
        getRowId={row => row.navInvoiceId}
        loading={invoicesState.loading === "is-loading"}
        sortModel={sortModel ? [sortModel] : undefined}
        onSortModelChange={model => {
          setSortModel(model[0]);
        }}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="server"
        pageSizeOptions={[
          { label: "5", value: 5 },
          { label: "10", value: 10 },
          { label: "20", value: 20 },
          { label: "50", value: 50 },
          { label: "100", value: 100 }
        ]}
        onRowDoubleClick={(params: GridRowParams) => setInvoiceLinesDialog({ selectedInvoiceId: params.row.navInvoiceId })}
        onCellKeyDown={(rowEvent, e) => {
          if (e.key === "Enter" && rowEvent.colDef.field !== "actions") setInvoiceLinesDialog({ selectedInvoiceId: rowEvent.row.navInvoiceId });
        }}
        sx={{
          overflow: "auto",
          color: theme.palette.customColors.darkGrey,
          background: theme.palette.customColors.white,
          borderRadius: 1,
          fontWeight: "normal",
          "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
            fontWeight: theme.typography.subtitle1.fontWeight,
            fontSize: theme.typography.subtitle1.fontSize
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: theme.typography.fontWeightBold
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden"
          }
        }}
      />

      {invoiceLinesDialogState?.selectedInvoiceId && <InvoiceLinesDialog />}
    </Stack>
  );
};

export const InvoiceHistory = connect(mapStateToProps, ownActions)(InvoiceHistoryBase);
